import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { arenaRequest } from './api.jsx';
import { Ji, ma } from './ArenaMedia.jsx';
import { de } from './ArenaDialog.jsx';
import { IconSparkles } from '../../components/icons.jsx';
import { EFFECT_STYLES } from './ArenaModifierIcon.jsx';
import { ArenaBattleCard } from './ArenaBattleCard.jsx';
import { ArenaMatchmaking } from './ArenaMatchmaking.jsx';
import { ArenaBattleResult } from './ArenaBattleResult.jsx';
import { ArenaBattleAnimation } from './ArenaBattleAnimation.jsx';
import { ArenaBattleHand } from './ArenaBattleHand.jsx';
import { ArenaBattleIntro } from './ArenaBattleIntro.jsx';
import { ArenaBattleLog } from './ArenaBattleLog.jsx';
import { MATCH_INTRO_MS, canPlayCard } from './hand-gesture.js';
import './battle-preview.css';
import './ArenaOnlineBattle.css';

const EMOTES = ['😄', '😤', '😭', '💜', '👍', '😱'];
function Formation({ side, canSelect, selected, onSelect, animation, tableRef, dropReady, placementReady }) {
  const played = side.cards.filter(card => card.used);
  return <div ref={tableRef} className={`battle-formation battle-table${dropReady ? ' battle-table-ready' : ''}`}>
    {!played.length && <span className="battle-table-hint">{tableRef ? 'Перетащите карту сюда' : ''}</span>}
    {played.map(card => <ArenaBattleCard card={card} type="button" key={card.slot}
      data-slot={card.slot}
      className={`battle-card ${de[Ji(card.rarity)] || ''} ${card.used ? 'battle-card-used' : ''} ${selected === card.slot ? 'battle-card-selected' : ''} ${animation?.slot === card.slot ? 'battle-card-attacking' : ''}`}
      disabled={!canSelect || card.used || card.hidden}
      onClick={() => onSelect(card.slot)} aria-pressed={selected === card.slot}
      aria-label={`${card.name}. ${card.modifier || 'Без модификатора'}. Урон ${card.damage}. Сыграна`}>
      <span className="battle-slot">{card.slot}</span>
    </ArenaBattleCard>)}
    {placementReady && played.length < 5 && <div className={`battle-placement${dropReady ? ' battle-placement-active' : ''}`} aria-hidden="true">
      <span>✦</span><small>{dropReady ? 'Отпустите' : 'Сюда'}</small>
    </div>}
  </div>;
}

function Combatant({ side, enemy, turn, ownTurn, ended, emote, animation, children }) {
  const [effectHint, setEffectHint] = useState(null);
  useEffect(() => {
    if (!effectHint) return;
    const timer = window.setTimeout(() => setEffectHint(null), 3000);
    return () => window.clearTimeout(timer);
  }, [effectHint]);
  const maxHp = side.maxHp || 100_000;
  const eventRole = animation && Boolean(enemy) === animation.isMe ? 'defender' : 'attacker';
  const kindToModifier = { heal: 'Исцеление', blood: 'Кровотечение', cry: 'Боевой клич', suppressed: 'Оглушение', invulnerable: 'Неуязвимость', mirror: 'Зеркало' };
  return <section className={`battle-combatant ${enemy ? 'battle-opponent' : 'battle-player'}`}>
    <div className="battle-player-info">
      <div className={`battle-identity${animation?.damage > 0 && eventRole === 'defender' ? ' battle-identity-hit' : ''}`}>{children || <span className="battle-avatar">{enemy ? '✦' : '✿'}</span>}
        <div className="battle-player-summary">
          <div className="battle-player-heading"><b title={side.name}>{side.name}</b><small className="battle-player-rating" aria-label={`Рейтинг ${ma(side.rating)}`}><span aria-hidden="true">🏆</span> {ma(side.rating)}</small></div>
          <div className="battle-health" role="meter" aria-label={enemy ? 'HP противника' : 'Ваши HP'}
            aria-valuenow={Math.min(maxHp, Math.max(0, side.hp))} aria-valuemin={0} aria-valuemax={maxHp}>
            <span className="battle-health-track"><span className="battle-health-fill" style={{ width: `${Math.min(100, Math.max(0, side.hp / maxHp * 100))}%` }} /></span>
            <b><span className="battle-health-heart" aria-hidden="true">♥</span> {ma(Math.max(0, side.hp))} <span>/ {ma(maxHp)}</span></b>
          </div>
        </div>
      </div>
      <div className="battle-effects" aria-label={`Модификаторы игрока ${side.name}`}>{!ended && side.effects.filter(effect => effect.starts <= turn && effect.expires >= turn && !(effect.type === 'Оглушение' && effect.native_blocked)).map((effect, index) => {
        const onOwnTurn = ['Исцеление', 'Кровотечение', 'Боевой клич', 'Оглушение'].includes(effect.type);
        const nextUse = ownTurn === onOwnTurn ? turn : turn + 1;
        const remainingUses = Math.max(0, Math.floor((Math.min(10, effect.expires) - nextUse) / 2) + 1);
        if (!remainingUses) return null;
        const lastUse = remainingUses === 1;
        const key = `${effect.type}:${effect.starts}:${effect.expires}:${index}`;
        const [tone, Icon] = EFFECT_STYLES[effect.type] || ['special', IconSparkles];
        const hint = `${effect.type}${lastUse ? ' · Последнее использование' : ''}`;
        const triggered = animation?.effects?.some(item => item.side === eventRole && (item.modifier || kindToModifier[item.kind]) === effect.type);
        return <button type="button" className={`battle-effect battle-effect-${tone}${lastUse ? ' battle-effect-last' : ''}${triggered ? ' battle-effect-triggered' : ''}`} key={key}
          title={hint} aria-label={hint} aria-expanded={effectHint === key}
          onClick={() => setEffectHint(effectHint === key ? null : key)} onBlur={() => setEffectHint(null)}>
          <Icon aria-hidden="true" />
          <small className="battle-effect-count" aria-hidden="true">{remainingUses}</small>
          {effectHint === key && <span className="battle-effect-tooltip" role="tooltip">{hint}</span>}
        </button>;
      })}</div>
    </div>
    {emote && <div className="battle-reaction" role="status" aria-label={`Эмоция игрока ${side.name}`}><span key={emote.id}>{emote.emoji}</span></div>}
  </section>;
}

export function ArenaOnlineBattle({ onClose, onFinished, battleId = null }) {
  const dialog = useRef(null);
  const board = useRef(null);
  const table = useRef(null);
  const introSeen = useRef(null);
  const [introUntil, setIntroUntil] = useState(0);
  const [dropReady, setDropReady] = useState(false);
  const resultDialog = useRef(null);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultError, setResultError] = useState('');
  const alive = useRef(true);
  const requestBusy = useRef(false);
  const requestGeneration = useRef(0);
  const session = useRef(null);
  const id = useRef(battleId);
  const finishedReported = useRef(false);
  const startAttempted = useRef(false);
  const serverOffset = useRef(0);
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(Date.now());
  const [showLog, setShowLog] = useState(false);
  const [surrenderOpen, setSurrenderOpen] = useState(false);
  const [pinsOpen, setPinsOpen] = useState(false);
  const [emoteBusy, setEmoteBusy] = useState(false);
  const emotePending = useRef(false);
  const battle = state?.battle;
  const ended = battle?.status === 'completed';
  const previousTurn = useRef(null);
  const pinControl = useRef(null);
  const [animationEvent, setAnimationEvent] = useState(null);

  function accept(next) {
    if (!alive.current) return;
    const current = session.current?.battle;
    if (current && (!next.battle || (current.battleId === next.battle.battleId && current.revision > next.battle.revision))) return;
    const newMove = current && next.battle?.events.length > current.events.length;
    if (newMove) setAnimationEvent(next.battle.events.at(-1));
    if (next.serverTime) serverOffset.current = Date.parse(next.serverTime) - Date.now();
    if (next.battle) id.current = next.battle.battleId;
    if (next.battle && introSeen.current !== next.battle.battleId) {
      introSeen.current = next.battle.battleId;
      if (next.battle.status === 'ongoing' && next.battle.turn === 1 && !next.battle.events.length) {
        setIntroUntil(Date.now() + MATCH_INTRO_MS);
      }
    }
    session.current = next;
    setState(next);
    if (next.battle?.status === 'completed' && !finishedReported.current) {
      finishedReported.current = true;
      setResultOpen(!newMove);
      onFinished?.();
    }
  }

  useEffect(() => {
    if (!ended || !resultDialog.current) return;
    if (resultOpen) resultDialog.current.showModal();
    else resultDialog.current.close();
  }, [ended, resultOpen]);
  useEffect(() => {
    alive.current = true;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.showModal();
    const controller = new AbortController();
    let timeout;
    let stopped = false;
    async function poll() {
      const generation = requestGeneration.current;
      try {
        if (!requestBusy.current && document.visibilityState !== 'hidden') {
          const next = await arenaRequest('battle/state', id.current ? { battleId: id.current } : {}, controller.signal);
          if (stopped || generation !== requestGeneration.current) return;
          accept(next);
          if (!next.battle && !next.queue && !startAttempted.current && !battleId) {
            startAttempted.current = true;
            requestBusy.current = true;
            try { accept(await arenaRequest('battle/search', {}, controller.signal)); }
            finally { requestBusy.current = false; }
          }
          setError('');
        }
      } catch (caught) {
        if (!stopped && generation === requestGeneration.current && !controller.signal.aborted) setError(caught.message || 'Не удалось обновить бой');
      } finally {
        if (!stopped) timeout = window.setTimeout(poll, 1200);
      }
    }
    poll();
    const timer = window.setInterval(() => setClock(Date.now()), 250);
    return () => {
      stopped = true; alive.current = false; controller.abort();
      window.clearTimeout(timeout); window.clearInterval(timer);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (previousTurn.current !== battle?.turn) { setSelected(null); previousTurn.current = battle?.turn; }
  }, [battle?.turn]);
  useEffect(() => {
    if (!animationEvent) return;
    const timer = window.setTimeout(() => setAnimationEvent(null), 2500 + Math.min(1000, (animationEvent.effects?.length || 0) * 95));
    return () => window.clearTimeout(timer);
  }, [animationEvent]);
  useEffect(() => {
    if (!ended || !animationEvent || battle?.reason === 'surrender') return;
    const timer = window.setTimeout(() => setResultOpen(true), 2000);
    return () => window.clearTimeout(timer);
  }, [ended, animationEvent, battle?.reason]);

  async function command(operation, payload = {}) {
    if (requestBusy.current) return;
    requestBusy.current = true; setBusy(true); setError('');
    try {
      accept(await arenaRequest(`battle/${operation}`, payload));
      setSelected(null); setSurrenderOpen(false);
    } catch (caught) { if (alive.current) setError(caught.message); }
    finally { requestBusy.current = false; if (alive.current) setBusy(false); }
  }

  async function sendEmote(emoji) {
    if (emotePending.current || !battle || ended) return;
    emotePending.current = true;
    setEmoteBusy(true);
    setPinsOpen(false);
    try {
      accept(await arenaRequest('battle/emote', { battleId: battle.battleId, emoji }));
    } catch (caught) {
      if (alive.current) setError(caught.message || 'Не удалось отправить пин');
    } finally {
      emotePending.current = false;
      if (alive.current) setEmoteBusy(false);
    }
  }

  async function playAgain() {
    if (requestBusy.current || session.current?.battle?.status !== 'completed') return;
    requestBusy.current = true;
    requestGeneration.current += 1;
    setBusy(true);
    setResultError('');
    try {
      const next = await arenaRequest('battle/search');
      if (!alive.current) return;
      session.current = null;
      id.current = null;
      finishedReported.current = false;
      startAttempted.current = true;
      introSeen.current = null;
      previousTurn.current = null;
      setIntroUntil(0);
      setAnimationEvent(null);
      setSelected(null);
      setShowLog(false);
      setPinsOpen(false);
      setResultOpen(false);
      accept(next);
    } catch (caught) {
      if (alive.current) setResultError(caught.message || 'Не удалось начать новый поиск');
    } finally {
      requestBusy.current = false;
      if (alive.current) setBusy(false);
    }
  }

  async function exit() {
    if (requestBusy.current) return;
    if (session.current?.queue) {
      requestBusy.current = true; setBusy(true);
      try {
        const next = await arenaRequest('battle/cancel');
        accept(next);
        // Matching won the race: show the assigned battle, do not abandon it.
        if (!next.battle) onClose();
      } catch (caught) { if (alive.current) setError(caught.message); }
      finally { requestBusy.current = false; if (alive.current) setBusy(false); }
    } else onClose();
  }

  const remaining = battle ? Math.max(0, Math.ceil((Date.parse(battle.deadline) - clock - serverOffset.current) / 1000)) : 0;
  const intro = !ended && clock < introUntil;
  const canPlay = battle?.status === 'ongoing' && battle.myTurn && !busy && remaining > 0 && !intro;
  function playCard(slot) {
    if (!canPlayCard(session.current?.battle, slot, requestBusy.current, remaining, intro)) return;
    command('attack', { battleId: battle.battleId, turn: battle.turn, slot });
  }
  const event = animationEvent;
  const serverClock = clock + serverOffset.current;
  const ownEmote = !ended && battle?.me.emote && Date.parse(battle.me.emote.expiresAt) > serverClock ? battle.me.emote : null;
  const opponentEmote = !ended && battle?.opponent.emote && Date.parse(battle.opponent.emote.expiresAt) > serverClock ? battle.opponent.emote : null;
  const emoteCoolingDown = Date.parse(battle?.me.emote?.availableAt) > serverClock;
  return createPortal(<dialog ref={dialog} className={`battle-preview battle-online${!battle ? ' battle-matchmaking' : ''}`} aria-label="Онлайн-арена"
    onCancel={event => { event.preventDefault(); if (surrenderOpen) setSurrenderOpen(false); else if (pinsOpen) setPinsOpen(false); else exit(); }}>
    <div className="battle-layout" onPointerDown={event => {
      if (pinsOpen && !pinControl.current?.contains(event.target)) setPinsOpen(false);
    }}>
      {error && <p className="battle-error" role="alert">{error}</p>}
      {!battle ? <ArenaMatchmaking queued={Boolean(state?.queue)} connected={Boolean(state)} busy={busy}
        elapsed={state?.queue ? Math.max(0, Math.floor((clock + serverOffset.current - Date.parse(state.queue.startedAt)) / 1000)) : 0}
        onCancel={exit} onStart={() => command('search')} /> : <>
        <div className="battle-board" ref={board} inert={intro ? true : undefined}>
          <div className={`battle-half battle-zone battle-zone-opponent battle-zone-${['stone-red', 'stone-blue'].includes(battle.opponent.arenaSkin) ? battle.opponent.arenaSkin : 'stone-red'}`}>
            <Combatant side={battle.opponent} enemy turn={battle.turn} ownTurn={!battle.myTurn} ended={ended} emote={opponentEmote} animation={event} />
            <Formation side={battle.opponent} canSelect={false} onSelect={() => {}} animation={!event?.isMe ? event : null} />
          </div>
          <div className={`battle-divider${battle.myTurn && !ended ? ' battle-divider-own' : ''}`}>
            <span className="battle-center-mark" aria-hidden="true">◆</span>
            <div className="battle-turn-status" role="status"><span>{ended ? 'Бой завершён' : `${battle.myTurn ? 'Ваш ход' : 'Ход противника'} · ${battle.turn}/10`}</span>{!ended && <b>{remaining ? `${remaining} сек.` : 'Автоход…'}</b>}</div>
          </div>
          <div className={`battle-half battle-zone battle-zone-player battle-zone-${['stone-red', 'stone-blue'].includes(battle.me.arenaSkin) ? battle.me.arenaSkin : 'stone-blue'}`}>
            <Formation side={battle.me} canSelect={false} selected={selected} onSelect={setSelected} animation={event?.isMe ? event : null} tableRef={table} dropReady={dropReady} placementReady={canPlay && selected != null} />
            <Combatant side={battle.me} turn={battle.turn} ownTurn={battle.myTurn} ended={ended} emote={ownEmote} animation={event}>
              <div className="battle-pin-control" ref={pinControl}>
                <button className="battle-avatar battle-emote-toggle" type="button" aria-label="Выбрать пин" disabled={ended || emoteBusy || emoteCoolingDown} onClick={() => setPinsOpen(!pinsOpen)} aria-expanded={pinsOpen}>
                  <span aria-hidden="true">✿</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-8l-5 3v-3H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" /></svg>
                </button>
                {pinsOpen && <section className="battle-emotes" aria-label="Эмоции"><header>Эмоции</header><div>{EMOTES.map(pin => <button key={pin} type="button" aria-label={`Пин ${pin}`} disabled={ended || emoteBusy || emoteCoolingDown} onClick={() => sendEmote(pin)}>{pin}</button>)}</div></section>}
              </div>
            </Combatant>
          </div>
          {event && <ArenaBattleAnimation key={`${battle.battleId}:${event.turn}`} event={event} boardRef={board} />}
        </div>
        {intro && <ArenaBattleIntro me={battle.me} opponent={battle.opponent} />}
        {ended && <dialog ref={resultDialog} className="battle-result-modal" aria-labelledby="battle-result-title"
          onCancel={event => { event.preventDefault(); event.stopPropagation(); if (!busy) setResultOpen(false); }}>
          <ArenaBattleResult result={battle.result} ratingDelta={battle.me.ratingDelta ?? 0} rewards={battle.me.rewards}
            onViewBattle={() => setResultOpen(false)} onClose={onClose} onPlayAgain={playAgain} busy={busy} error={resultError} />
        </dialog>}
        {!ended && <ArenaBattleHand cards={battle.me.cards} selected={selected} onSelect={setSelected}
          canPlay={canPlay} onPlay={playCard} tableRef={table} onDragChange={setDropReady} intro={intro} />}
        <footer className="battle-footer battle-online-footer" inert={intro ? true : undefined}>
          <button type="button" onClick={() => setShowLog(!showLog)} aria-expanded={showLog}>Ходы ({battle.events.length})</button>
          {ended && <button type="button" onClick={() => setResultOpen(true)}>Результат</button>}
          {!ended && <button type="button" disabled={busy} onClick={() => setSurrenderOpen(true)}>Сдаться</button>}
        </footer>
        {showLog && <ArenaBattleLog battle={battle} onClose={() => setShowLog(false)} />}
        {surrenderOpen && <div className="battle-confirm"><section className="battle-surrender-card" role="alertdialog" aria-labelledby="battle-surrender-title" aria-describedby="battle-surrender-description">
          <span className="battle-surrender-symbol" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V4M5 4c5-4 9 4 14 0v11c-5 4-9-4-14 0" /></svg></span>
          <h2 id="battle-surrender-title">Сдаться?</h2>
          <p id="battle-surrender-description">Бой завершится поражением.<br />Вы ещё можете продолжить сражение.</p>
          <div className="battle-surrender-actions">
            <button className="battle-surrender-continue" type="button" disabled={busy} onClick={() => setSurrenderOpen(false)}>Продолжить бой</button>
            <button className="battle-surrender-submit" type="button" disabled={busy} onClick={() => command('surrender', { battleId: battle.battleId })}>{busy ? 'Завершаем…' : 'Сдаться'}</button>
          </div>
        </section></div>}
      </>}
    </div>
  </dialog>, document.body);
}
